import { SafePromise } from '@benzinga/safe-await';
import { Subscribable, Subscription } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';
import { oneRequestAtATime } from '@benzinga/utils';

import {
  GovernmentMember,
  GovernmentTrade,
  MembersOverview,
  MonthlyOverview,
  Overview,
  SecuritiesOverview,
  Security,
  TradingPerformance,
  TradesQuery,
} from './entities';
import { GovTradesRequest } from './request';
import { GovTradesStore } from './store';

type GovTradesEventMap = {
  'gov_trades:update_members': { members: MembersOverview };
  'gov_trades:get_members_error': { error: Error | string };
  'gov_trades:update_member_trades': { tradingPerformance: TradingPerformance[] };
  'gov_trades:update_member_trades_error': { error: Error | string };
  'gov_trades:update_trades': { trades: GovernmentTrade[] };
  'gov_trades:get_trades_error': { error: Error | string };
  'gov_trades:get_reports_error': { error: Error | string };
  'gov_trades:get_reports': { reports: GovernmentTrade[] };
  'gov_trades:get_overview_error': { error: Error | string };
  'gov_trades:get_overview': { overview: Overview[] };
  'gov_trades:get_overview_monthly_error': { error: Error | string };
  'gov_trades:get_overview_monthly': { overview: MonthlyOverview[] };
  'gov_trades:get_security_error': { error: Error | string };
  'gov_trades:get_security': { security: Security[] };
  'gov_trades:get_securities_error': { error: Error | string };
  'gov_trades:get_securities': { securities: SecuritiesOverview[] };
  'gov_trades:get_trading_performance_error': { error: Error | string };
  'gov_trades:get_congress_trading_performance': { tradingPerformance: TradingPerformance[] };
};

export type GovTradesManagerEvent = {
  [K in keyof GovTradesEventMap]: { type: K } & GovTradesEventMap[K];
}[keyof GovTradesEventMap];

export const getMostUpdated = (arr: any[]) => {
  if (!arr || arr.length === 0) return null;
  if (arr.length === 1) return arr[0];
  return arr.reduce((acc, curr) => {
    return acc.updated > curr.updated ? acc : curr;
  });
};

export class GovTradesManager extends Subscribable<GovTradesManagerEvent> {
  private request: GovTradesRequest;
  private requestSubscription?: Subscription<GovTradesRequest>;
  private storeSubscription?: Subscription<GovTradesStore>;
  private store: GovTradesStore;
  private isFetching = false;

  private fetchMembers = oneRequestAtATime(async (): SafePromise<MembersOverview> => {
    const members = await this.request.getMembers();
    if (members.err) {
      return { err: members.err };
    }
    return { ok: await this.store.updateMembers(members.ok[0]) };
  });

  constructor(session: Session) {
    super();
    this.request = new GovTradesRequest(session);
    this.store = new GovTradesStore();
    session.onPostSessionClose(() => this.requestSubscription?.unsubscribe());
    this.fetchMembers();
  }

  public static getName = () => 'benzinga-gov-trades';

  public getMembers(): MembersOverview;
  public getMembers(options: { waitForData: true }): SafePromise<MembersOverview>;
  public getMembers(options?: { waitForData?: boolean }): MembersOverview | SafePromise<MembersOverview> {
    const cachedMembers = this.store.getCachedMembers();
    const hasCachedMembers = Object.keys(cachedMembers).length > 0;

    if (options?.waitForData) {
      if (hasCachedMembers) {
        return Promise.resolve({ ok: cachedMembers });
      }
      return this.fetchMembers();
    }

    if (!hasCachedMembers && !this.isFetching) {
      this.fetchMembers();
    }
    return cachedMembers;
  }

  public getTrades = async (query: TradesQuery = {}): SafePromise<GovernmentTrade[]> => {
    const response = await this.request.getTrades(query);
    if (response.err) {
      return { err: response.err };
    }
    return { ok: response.ok.data };
  };

  public getTradingPerformanceByMember = async (id: string): SafePromise<TradingPerformance> => {
    const response = await this.request.getTradingPerformanceByMember(id);

    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getStoredMember = (id: string) => {
    const cachedGovernmentMemberList = this.store.getCachedGovernmentMemberList();
    const member = cachedGovernmentMemberList.find(member => member.filer_info.member_id === id);
    return member;
  };
  public getMember = async (id: string): SafePromise<GovernmentMember | undefined> => {
    const cachedMember = this.getStoredMember(id);
    if (!cachedMember) {
      const response = await this.request.getMember(id);
      if (response.err) return { err: response.err };
      this.store.updateGovernmentMemberCachedList(response.ok);
      return { ok: response.ok };
    }

    return { ok: cachedMember };
  };

  public getReports = async (): SafePromise<{ data: GovernmentTrade[] }> => {
    const response = await this.request.getReports();
    if (response.ok) {
      return { ok: response?.ok };
    } else {
      return { err: response.err };
    }
  };

  public getOverview = async (): SafePromise<Overview> => {
    const response = await this.request.getOverview();
    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getOverviewMonthly = async (): SafePromise<MonthlyOverview[]> => {
    const response = await this.request.getOverviewMonthly();
    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getSecurity = async (id: string): SafePromise<Security> => {
    const response = await this.request.getSecurity(id);
    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getSecurities = async (): SafePromise<SecuritiesOverview> => {
    const response = await this.request.getSecurities();
    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getCongressTradingPerformance = async (): SafePromise<TradingPerformance> => {
    const response = await this.request.getCongressTradingPerformance();
    if (response.ok) {
      return { ok: getMostUpdated(response?.ok) };
    } else {
      return { err: response.err };
    }
  };

  public getSecuritiesList = async (): SafePromise<Security[]> => {
    const response = await this.request.getSecurities();
    if (response.ok) {
      const securitiesOverview = getMostUpdated(response?.ok);
      const list = securitiesOverview?.most_active?.map(security => {
        return {
          ...security,
          ticker: security.symbol,
        };
      });
      return { ok: list };
    } else {
      return { err: response.err };
    }
  };

  protected override onFirstSubscription = (): void => {
    this.requestSubscription = this.request.subscribe(event => this.dispatch(event));
    this.storeSubscription = this.store.subscribe(event => this.dispatch(event));
  };

  protected override onZeroSubscriptions = (): void => {
    this.requestSubscription?.unsubscribe();
    this.storeSubscription?.unsubscribe();
  };
}
